import { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Upload, Calendar, Users, Video, X, Loader2, Play, Clock, Eye, Cloud } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { z } from 'zod';
import { MediaPlayer } from '@/components/media/MediaPlayer';
import { uploadToS3, listUserContent, deleteFromS3, getSignedUrl } from '@/utils/aws/s3Manager';

const clipSchema = z.object({
  title: z.string().trim().min(3, 'Title must be at least 3 characters').max(100, 'Title must be less than 100 characters'),
  description: z.string().trim().max(500, 'Description must be less than 500 characters').optional(),
  category: z.string().min(1, 'Category is required'),
});

const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo', 'video/avi'];

interface Clip {
  id: string;
  title: string;
  description: string | null;
  video_url: string | null; // stores storage path or URL
  thumbnail_url: string | null;
  duration: number;
  view_count: number;
  created_at: string;
  creator_id: string;
  creatorProfile?: {
    username: string;
    avatar_url: string | null;
  } | null;
}

export const ContentManager = () => {
  const { user } = useAuth();

  // Upload state
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Events state
  const [eventTitle, setEventTitle] = useState('');
  const [eventDate, setEventDate] = useState('');

  // Clips state
  const [clips, setClips] = useState<Clip[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadClips();
  }, [user]);

  const loadClips = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data: clipsData, error } = await supabase
        .from('recordings')
        .select('*')
        .eq('creator_id', user.id)
        .eq('is_published', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (!clipsData || clipsData.length === 0) {
        setClips([]);
        return;
      }

      const creatorIds = [...new Set(clipsData.map((c) => c.creator_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, username, avatar_url')
        .in('id', creatorIds);

      const profileMap = new Map((profiles || []).map((p: any) => [p.id, p]));
      const enriched: Clip[] = clipsData.map((c: any) => ({
        ...c,
        creatorProfile: profileMap.get(c.creator_id) || null,
      }));
      setClips(enriched);
    } catch (err) {
      console.error('Error loading clips:', err);
      toast.error('Failed to load clips');
    } finally {
      setLoading(false);
    }
  };

  const validateFile = (file: File): boolean => {
    if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
      toast.error('Invalid file type. Please upload MP4, WebM, MOV, or AVI files.');
      return false;
    }
    return true;
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (validateFile(droppedFile)) setFile(droppedFile);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (validateFile(selectedFile)) setFile(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file || !user) return;

    // Validate form data
    try {
      clipSchema.parse({ title, description, category });
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast.error(error.errors[0].message);
        return;
      }
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Upload to AWS S3 via our utility
      const filename = await uploadToS3({
        file,
        folder: 'recordings',
        onProgress: setUploadProgress,
      });

      // Read duration via blob URL
      const video = document.createElement('video');
      video.preload = 'metadata';
      const duration = await new Promise<number>((resolve) => {
        video.onloadedmetadata = () => resolve(Math.floor(video.duration));
        video.onerror = () => resolve(0);
        video.src = URL.createObjectURL(file);
      });

      // Store in database with S3 path
      const { error: dbError } = await supabase
        .from('recordings')
        .insert({
          creator_id: user.id,
          stream_id: '00000000-0000-0000-0000-000000000000',
          title: title.trim(),
          description: description.trim() || null,
          video_url: filename,
          file_size: file.size,
          duration,
          status: 'completed',
          is_published: true,
          published_at: new Date().toISOString(),
        });

      if (dbError) throw dbError;

      toast.success('✅ Clip uploaded to AWS S3 successfully!');

      // Reset form
      setFile(null);
      setTitle('');
      setDescription('');
      setCategory('');
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadClips();
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(error?.message || 'Failed to upload clip');
    } finally {
      setUploading(false);
    }
  };

  const removeFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleScheduleEvent = () => {
    if (!eventTitle || !eventDate) {
      toast.error('Please fill in all fields');
      return;
    }
    toast.success('Event scheduled!');
    setEventTitle('');
    setEventDate('');
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Content Management</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="upload">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="upload">
              <Cloud className="h-4 w-4 mr-2" />
              Upload to S3
            </TabsTrigger>
            <TabsTrigger value="clips">
              <Video className="h-4 w-4 mr-2" />
              My Clips
            </TabsTrigger>
            <TabsTrigger value="events">
              <Calendar className="h-4 w-4 mr-2" />
              Events
            </TabsTrigger>
            <TabsTrigger value="membership">
              <Users className="h-4 w-4 mr-2" />
              Fan Club
            </TabsTrigger>
          </TabsList>

          {/* Upload Tab */}
          <TabsContent value="upload" className="space-y-6">
            {!file ? (
              <div
                className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
                  onChange={handleFileChange}
                  className="hidden"
                  disabled={uploading}
                />
                <div className="flex flex-col items-center gap-4">
                  <div className="p-4 bg-primary/10 rounded-full">
                    <Upload className="h-8 w-8 text-primary" />
                  </div>
                  <div>
                    <p className="text-lg font-medium">Drop your video here</p>
                    <p className="text-sm text-muted-foreground mt-1">or click to browse files</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    Select Video
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    Supported formats: MP4, WebM, MOV, AVI
                  </p>
                </div>
              </div>
            ) : (
              <div className="border rounded-lg p-4">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <Video className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{file.name}</p>
                    <p className="text-sm text-muted-foreground">{(file.size / (1024 * 1024)).toFixed(2)} MB</p>
                  </div>
                  <Button type="button" variant="ghost" size="icon" onClick={removeFile} disabled={uploading}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {uploading && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <Progress value={uploadProgress} />
              </div>
            )}

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">
                  Title <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter clip title"
                  maxLength={100}
                  disabled={uploading}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">
                  Category <span className="text-destructive">*</span>
                </Label>
                <select
                  id="category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={uploading}
                  required
                >
                  <option value="">Select category</option>
                  <option value="gaming">Gaming</option>
                  <option value="music">Music</option>
                  <option value="talk">Talk</option>
                  <option value="creative">Creative</option>
                  <option value="entertainment">Entertainment</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe your clip (optional)"
                  maxLength={500}
                  rows={3}
                  disabled={uploading}
                />
                <p className="text-xs text-muted-foreground text-right">{description.length}/500</p>
              </div>
            </div>

            <Button onClick={handleUpload} disabled={!file || !title || !category || uploading} className="w-full" size="lg">
              {uploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Upload Clip
                </>
              )}
            </Button>
          </TabsContent>

          {/* Clips Tab */}
          <TabsContent value="clips" className="space-y-4">
            {loading ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="space-y-3">
                    <Skeleton className="aspect-video rounded-lg" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                ))}
              </div>
            ) : clips.length === 0 ? (
              <div className="text-center py-12">
                <Video className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground mb-2">No clips uploaded yet</p>
                <p className="text-sm text-muted-foreground">Upload your first clip to get started</p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {clips.map((clip) => (
                  <Card key={clip.id} className="overflow-hidden hover:shadow-lg transition-shadow p-0">
                    <div className="relative aspect-video bg-muted">
                      {clip.thumbnail_url ? (
                        <img src={clip.thumbnail_url} alt={clip.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Video className="h-12 w-12 text-muted-foreground" />
                        </div>
                      )}
                      <div className="absolute bottom-2 right-2 bg-background/90 backdrop-blur-sm px-2 py-1 rounded text-xs font-medium">
                        {formatDuration(clip.duration)}
                      </div>
                    </div>
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-start gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={clip.creatorProfile?.avatar_url || ''} />
                          <AvatarFallback>
                            {clip.creatorProfile?.username?.[0]?.toUpperCase() || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium line-clamp-2 text-sm">{clip.title}</h4>
                          <p className="text-xs text-muted-foreground">@{clip.creatorProfile?.username || 'Unknown'}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Eye className="h-3 w-3" />
                          {clip.view_count}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(clip.created_at).toLocaleDateString()}
                        </div>
                      </div>

                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="secondary" size="sm" className="w-full">
                            <Play className="mr-2 h-4 w-4" /> Play
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-3xl">
                          <DialogHeader>
                            <DialogTitle>{clip.title}</DialogTitle>
                          </DialogHeader>
                          <MediaPlayer src={clip.video_url || undefined} bucket="recordings" poster={clip.thumbnail_url || undefined} title={clip.title} />
                        </DialogContent>
                      </Dialog>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events" className="space-y-4">
            <div className="space-y-2">
              <Label>Event Title</Label>
              <Input
                placeholder="Q&A Session, Concert, etc."
                value={eventTitle}
                onChange={(e) => setEventTitle(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Date & Time</Label>
              <Input type="datetime-local" value={eventDate} onChange={(e) => setEventDate(e.target.value)} />
            </div>
            <Button onClick={handleScheduleEvent} className="w-full">
              Schedule Event
            </Button>

            <div className="pt-4 border-t">
              <h4 className="text-sm font-medium mb-3">Upcoming Events</h4>
              <div className="text-sm text-muted-foreground text-center py-4">No upcoming events</div>
            </div>
          </TabsContent>

          {/* Membership Tab */}
          <TabsContent value="membership" className="space-y-4">
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium">Fan Club Members</h4>
                  <span className="text-2xl font-bold">0</span>
                </div>
                <p className="text-sm text-muted-foreground">Total active subscribers</p>
              </div>

              <div className="space-y-2">
                <Label>Monthly Subscription Price</Label>
                <Input type="number" placeholder="e.g., 500" defaultValue="500" />
                <p className="text-xs text-muted-foreground">Price in coins</p>
              </div>

              <div className="space-y-2">
                <Label>Member Benefits</Label>
                <Input placeholder="Exclusive content access" />
                <Input placeholder="Early stream notifications" className="mt-2" />
                <Input placeholder="Members-only chat" className="mt-2" />
              </div>

              <Button className="w-full">Update Membership Settings</Button>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
